import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  useNotifications,
  useUnreadCount,
  useMarkAllRead,
  subscribeNotificationsChannel,
} from '@/lib/notifications';
import { NotificationItem } from '@/components/NotificationItem';
import { useQueryClient } from '@tanstack/react-query';

const MAX_SHOWN = 10;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const qc = useQueryClient();
  const unreadQuery = useUnreadCount();
  const listQuery = useNotifications({ limit: MAX_SHOWN });
  const markAll = useMarkAllRead();

  useEffect(() => subscribeNotificationsChannel(qc), [qc]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const count = unreadQuery.data?.count ?? 0;
  const actionCount = unreadQuery.data?.action_required_count ?? 0;
  const rows = listQuery.data?.notifications ?? [];
  const hasAction = actionCount > 0;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="relative grid h-9 w-9 place-items-center rounded-lg border border-rule text-ink-muted hover:border-rule-strong hover:text-ink"
      >
        <Bell className="h-4 w-4" />
        {count > 0 && (
          <span
            className={cn(
              'absolute -right-1 -top-1 min-w-[1.125rem] h-[1.125rem] px-1 rounded-full text-[0.625rem] font-semibold grid place-items-center ring-2 ring-surface',
              hasAction ? 'bg-danger text-white' : 'bg-brand-500 text-white'
            )}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Mobile scrim */}
          <div
            className="fixed inset-0 z-40 bg-black/20 sm:hidden"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-label="Notifications"
            className={cn(
              'z-50 bg-surface border border-rule shadow-lg',
              // Desktop: dropdown
              'sm:absolute sm:right-0 sm:top-[calc(100%+0.5rem)] sm:w-[22rem] sm:rounded-xl sm:max-h-[32rem] sm:overflow-hidden',
              // Mobile: bottom sheet
              'fixed inset-x-0 bottom-0 rounded-t-2xl max-h-[80vh] overflow-hidden sm:inset-x-auto sm:bottom-auto'
            )}
          >
            <div className="flex items-center justify-between border-b border-rule px-4 py-3">
              <div>
                <div className="text-[0.875rem] font-semibold text-ink">Notifications</div>
                {count > 0 && (
                  <div className="text-[0.6875rem] text-ink-muted">
                    {count} unread{actionCount > 0 && ` · ${actionCount} need action`}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {count > 0 && (
                  <button
                    type="button"
                    onClick={() => markAll.mutate()}
                    disabled={markAll.isPending}
                    className="text-[0.6875rem] font-medium text-ink-muted hover:text-ink"
                  >
                    Mark all read
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-y-auto max-h-[24rem] sm:max-h-[26rem] divide-y divide-rule">
              {listQuery.isLoading ? (
                <div className="px-4 py-8 text-center text-[0.8125rem] text-ink-muted">
                  Loading…
                </div>
              ) : rows.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <div className="text-[0.875rem] text-ink">You're all caught up.</div>
                  <div className="mt-1 text-[0.75rem] text-ink-muted">
                    Notifications that need your attention will appear here.
                  </div>
                </div>
              ) : (
                rows.map((row) => (
                  <NotificationItem key={row.id} row={row} onActivate={() => setOpen(false)} />
                ))
              )}
            </div>
            <div className="border-t border-rule px-4 py-2.5 text-right">
              <Link
                to="/notifications"
                onClick={() => setOpen(false)}
                className="text-[0.75rem] font-medium text-brand-700 hover:text-brand-800"
              >
                View all
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
